/**
 * UI Lockdown — dims and disables all UI except allowed selectors.
 * Used by enterprise lesson engine for guided lockdown pacing.
 */

let overlayEl: HTMLElement | null = null;
let observer: MutationObserver | null = null;
let currentAllowed: string[] = [];

export interface LockdownOptions {
  /** Draw the dimming overlay. Off for info steps, where the user is reading the
      panels and only needs interaction blocked, not the sim visually muted. */
  dim?: boolean;
}

export function applyLockdown(allowedSelectors: string[], options: LockdownOptions = {}): void {
  const { dim = true } = options;
  removeLockdown();
  currentAllowed = allowedSelectors;

  const simContainer = document.getElementById('sim-container');
  if (!simContainer) return;

  simContainer.classList.add('lesson-lockdown');

  // Add dimming overlay inside sim-container.
  // pointer-events: none lets clicks pass through to allowed elements.
  if (dim) {
    overlayEl = document.createElement('div');
    overlayEl.className = 'lockdown-overlay';
    simContainer.appendChild(overlayEl);
  }

  // Mark allowed elements
  markAllowed(simContainer, allowedSelectors);

  // Watch document.body for dynamically appended elements (menus, modals, dialogs)
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          checkAndMarkBodyChild(node, allowedSelectors);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true });
}

export function removeLockdown(): void {
  const simContainer = document.getElementById('sim-container');
  if (simContainer) {
    simContainer.classList.remove('lesson-lockdown');
  }

  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
  }

  // Remove all lockdown-allowed classes
  document.querySelectorAll('.lockdown-allowed').forEach((el) => {
    el.classList.remove('lockdown-allowed');
  });

  if (observer) {
    observer.disconnect();
    observer = null;
  }
  currentAllowed = [];
}

function markAllowed(root: HTMLElement, selectors: string[]): void {
  for (const selector of selectors) {
    root.querySelectorAll(selector).forEach((el) => {
      el.classList.add('lockdown-allowed');
    });
  }
  // Also check body-level elements (menus, modals already present)
  for (const selector of selectors) {
    document.body.querySelectorAll(`:scope > ${selector}`).forEach((el) => {
      el.classList.add('lockdown-allowed');
    });
  }
}

function checkAndMarkBodyChild(node: HTMLElement, selectors: string[]): void {
  for (const selector of selectors) {
    if (node.matches(selector)) {
      node.classList.add('lockdown-allowed');
    }
    node.querySelectorAll(selector).forEach((el) => {
      el.classList.add('lockdown-allowed');
    });
  }
  // Always allow enterprise lesson UI elements
  if (node.classList.contains('ent-breadcrumb-bar') ||
      node.classList.contains('ent-instruction-panel') ||
      node.classList.contains('ent-completion-overlay')) {
    node.classList.add('lockdown-allowed');
  }
}

/** Re-apply lockdown allowed classes (call after DOM changes) */
export function refreshLockdown(): void {
  if (currentAllowed.length === 0) return;
  const simContainer = document.getElementById('sim-container');
  if (simContainer) {
    markAllowed(simContainer, currentAllowed);
  }
}
